import { createContext, useContext, useCallback, useRef } from 'react';

interface NavigationGuardContextType {
    /** Register a guard that returns true if navigation should be blocked */
    registerGuard: (guard: () => boolean, onBlocked: (pendingAction?: () => void) => void) => () => void;
    /** Check all guards — returns true if navigation is allowed. Pass the action to execute if allowed/after saving. */
    checkNavigation: (pendingAction?: () => void) => boolean;
}

const NavigationGuardContext = createContext<NavigationGuardContextType>({
    registerGuard: () => () => { },
    checkNavigation: () => true,
});

export function useNavigationGuard() {
    return useContext(NavigationGuardContext);
}

export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
    const guardsRef = useRef<Map<symbol, { shouldBlock: () => boolean; onBlocked: (pendingAction?: () => void) => void }>>(new Map());

    const registerGuard = useCallback((shouldBlock: () => boolean, onBlocked: (pendingAction?: () => void) => void) => {
        const key = Symbol();
        guardsRef.current.set(key, { shouldBlock, onBlocked });
        return () => { guardsRef.current.delete(key); };
    }, []);

    const checkNavigation = useCallback((pendingAction?: () => void) => {
        for (const [, guard] of guardsRef.current) {
            if (guard.shouldBlock()) {
                guard.onBlocked(pendingAction);
                return false; // blocked
            }
        }
        // No guard blocked — execute the action immediately
        pendingAction?.();
        return true; // allowed
    }, []);

    return (
        <NavigationGuardContext.Provider value={{ registerGuard, checkNavigation }}>
            {children}
        </NavigationGuardContext.Provider>
    );
}

